import Alert from '@mui/material/Alert';
import { useBankSyncStatusQuery } from '../features/received-payments/queries';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Badge from '@mui/material/Badge';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PaymentsIcon from '@mui/icons-material/Payments';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { Paths } from '@shared/AppRoutes';
import { ImperativeDialog } from '@shared/components/dialogs/imperative-dialog';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import {
  useReceivedPaymentsQuery,
  useSyncReceivedPaymentsMutation,
  useDeletePendingPaymentMutation,
  useConfirmPendingPaymentMutation,
} from '../features/received-payments/queries';
import {
  QrPaymentType,
  ReceivedPayment,
  ReceivedPaymentStatus,
} from '../features/received-payments/entity';
import BoxIcon from '@shared/components/BoxIcon';

type PaymentFilter = 'ALL' | 'MATCHED' | 'PENDING';

const toQrPaymentType = (paymentType: ReceivedPayment['paymentType']): QrPaymentType | null => {
  if (!paymentType) {
    return null;
  }
  if (paymentType === 'service') {
    return 'servicesAndProducts';
  }
  if (paymentType === 'products') {
    return 'additional';
  }
  return paymentType;
};

const formatAmount = (amount: string, currency: string): string =>
  `${Number(amount).toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency}`;

const PaymentRow = ({
  payment,
  onCancelPending,
  onConfirmPending,
  isCancelling,
  isConfirming,
}: {
  payment: ReceivedPayment;
  onCancelPending: (payment: ReceivedPayment) => void;
  onConfirmPending: (payment: ReceivedPayment) => void;
  isCancelling: boolean;
  isConfirming: boolean;
}) => {
  const { t } = useIntlTranslations('receivedPayments');
  const cancelDialogRef = useRef<ImperativeFormDialogRefType | null>(null);
  const confirmDialogRef = useRef<ImperativeFormDialogRefType | null>(null);
  const isMatched = payment.status === ReceivedPaymentStatus.MATCHED;
  const isPending = payment.status === ReceivedPaymentStatus.PENDING;
  const canHandlePending = isPending && payment.matchedVisitId && payment.paymentType;

  const clientName = payment.matchedVisit
    ? [payment.matchedVisit.client.firstName, payment.matchedVisit.client.lastName]
        .filter(Boolean)
        .join(' ')
    : null;

  const paymentTypeLabel: Record<string, string> = {
    deposit: 'záloha',
    servicesAndProducts: 'služby a produkty',
    additional: 'doplňková platba',
    service: 'služby a produkty',
    products: 'doplňková platba',
  };

  const paymentStatusLabel = isMatched
    ? t('statusMatched')
    : isPending
      ? 'Čeká na banku'
      : t('statusUnmatched');

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: isMatched ? 'success.light' : isPending ? 'warning.light' : 'divider',
        backgroundColor: isPending ? 'warning.50' : undefined,
        borderRadius: 3,
      }}
    >
      <CardContent
        sx={{
          p: { xs: 1.25, sm: 2 },
          '&:last-child': {
            pb: { xs: 1.25, sm: 2 },
          },
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'row', md: 'row' }}
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            rowGap={1}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{
                  fontSize: '1rem',
                }}
              >
                {formatAmount(payment.amount, payment.currency)}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="flex-end"
            >
              <Chip
                color={isMatched ? 'success' : isPending ? 'warning' : 'default'}
                size="small"
                sx={{
                  height: { xs: 30, sm: 34 },
                  maxWidth: '100%',
                  '& .MuiChip-label': {
                    px: { xs: 0.75, sm: 1 },
                    fontSize: { xs: 10.5, sm: 12.5 },
                  },
                  '& .MuiChip-icon': {
                    fontSize: { xs: 15, sm: 17 },
                  },
                }}
                icon={
                  isMatched ? (
                    <CheckCircleIcon />
                  ) : isPending ? (
                    <HourglassEmptyIcon />
                  ) : (
                    <HelpOutlineIcon />
                  )
                }
                label={paymentStatusLabel}
              />

              {canHandlePending ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <BoxIcon
                    size="small"
                    boxColor="common.white"
                    disabled={isConfirming || isCancelling}
                    sx={{ color: 'success.main' }}
                    aria-label="Ručně potvrdit čekající platbu"
                    onClick={() => confirmDialogRef.current?.open()}
                    icon={<CheckCircleIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                  />

                  <BoxIcon
                    size="small"
                    boxColor="common.white"
                    disabled={isConfirming || isCancelling}
                    aria-label="Smazat čekající platbu"
                    sx={{ color: 'error.main' }}
                    onClick={() => cancelDialogRef.current?.open()}
                    icon={<DeleteOutlineIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                  />
                </Stack>
              ) : null}

              <ImperativeDialog
                ref={confirmDialogRef}
                title="Ručně potvrdit platbu?"
                dialogHelperText="Po potvrzení se čekající platba označí jako spárovaná."
                onConfirm={() => onConfirmPending(payment)}
              />

              <ImperativeDialog
                ref={cancelDialogRef}
                title="Smazat čekající platbu?"
                dialogHelperText="Tato akce odstraní čekající platbu pro vybranou návštěvu."
                onConfirm={() => onCancelPending(payment)}
              />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1.5}>
            <Stack
              spacing={{ xs: 1, sm: 2 }}
              sx={{
                flex: 1,
                minWidth: 0,
                pr: { lg: 2 },
                borderRight: { lg: '1px solid' },
                borderColor: { lg: 'divider' },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    pl: { xs: 1.25, sm: 2 },
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t('variableSymbol')}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      fontSize: {
                        xs: '0.75rem',
                      },
                    }}
                  >
                    {payment.variableSymbol ?? '—'}
                  </Typography>
                </Box>
              </Stack>

              {payment.paymentType && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      pl: { xs: 1.25, sm: 2 },
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Typ
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        fontSize: {
                          xs: '0.75rem',
                        },
                      }}
                    >
                      {paymentTypeLabel[payment.paymentType] ?? payment.paymentType}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Stack>
            <Stack
              spacing={{ xs: 1, sm: 2 }}
              sx={{
                flex: 1,
                minWidth: 0,
                pr: { lg: 2 },
                borderRight: { lg: '1px solid' },
                borderColor: { lg: 'divider' },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    pl: { xs: 1.25, sm: 2 },
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {isPending ? 'Čeká od' : t('counterAccount')}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      fontSize: {
                        xs: '0.75rem',
                      },
                    }}
                  >
                    {isPending
                      ? dayjs(payment.receivedAt).format('D. M. YYYY HH:mm')
                      : (payment.counterAccount ?? '—')}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                {payment.matchedVisit ? (
                  <Button
                    component={RouterLink}
                    to={Paths.visitDetail(payment.matchedVisit.client.id, payment.matchedVisit.id)}
                    variant="outlined"
                  >
                    {clientName || t('openVisit')}
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ReceivedPaymentsPage = () => {
  const { t } = useIntlTranslations('receivedPayments');
  const { data: payments, isLoading } = useReceivedPaymentsQuery(true, 30000);
  const syncMutation = useSyncReceivedPaymentsMutation();
  const syncStatus = useBankSyncStatusQuery();
  const recoveredSinceRequest = Boolean(
    syncStatus.data?.lastSuccessAt &&
    Date.parse(syncStatus.data.lastSuccessAt) > syncMutation.submittedAt,
  );
  const syncHasError =
    syncStatus.isError ||
    syncStatus.data?.hasError ||
    (!recoveredSinceRequest && (syncMutation.isError || Boolean(syncMutation.data?.failed)));
  const deletePendingMutation = useDeletePendingPaymentMutation();
  const confirmPendingMutation = useConfirmPendingPaymentMutation();
  const [filter, setFilter] = useState<PaymentFilter>('PENDING');

  useEffect(() => {
    syncMutation.mutate({ silent: true });
  }, []);

  const handleCancelPending = (payment: ReceivedPayment) => {
    const normalizedType = toQrPaymentType(payment.paymentType);
    if (!payment.matchedVisitId || !normalizedType) {
      return;
    }
    deletePendingMutation.mutate({
      visitId: payment.matchedVisitId,
      type: normalizedType,
    });
  };

  const handleConfirmPending = (payment: ReceivedPayment) => {
    const normalizedType = toQrPaymentType(payment.paymentType);
    if (!payment.matchedVisitId || !normalizedType) {
      return;
    }
    confirmPendingMutation.mutate({
      visitId: payment.matchedVisitId,
      type: normalizedType,
    });
  };

  const filteredPayments = useMemo(() => {
    if (!payments) {
      return [];
    }
    if (filter === 'MATCHED') {
      return payments.filter(p => p.status === ReceivedPaymentStatus.MATCHED);
    }

    if (filter === 'PENDING') {
      return payments.filter(p => p.status === ReceivedPaymentStatus.PENDING);
    }
    return payments;
  }, [payments, filter]);

  const pendingCount = useMemo(
    () => (payments ?? []).filter(p => p.status === ReceivedPaymentStatus.PENDING).length,
    [payments],
  );

  return (
    <Stack spacing={2}>
      {syncHasError ? (
        <Alert severity="warning">
          Synchronizace plateb se nepodařila dokončit. Nezpracované zprávy se zkusí znovu
          automaticky. Můžete také použít tlačítko synchronizace.
        </Alert>
      ) : null}
      <Typography variant="caption" color="text.secondary" role="status">
        {syncStatus.data?.running
          ? 'Synchronizace právě probíhá…'
          : syncStatus.data?.lastSuccessAt
            ? `Poslední dokončená synchronizace: ${dayjs(syncStatus.data.lastSuccessAt).format('D. M. YYYY HH:mm')}`
            : 'Synchronizace zatím nebyla dokončena.'}
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1}
      >
        <Typography variant="h5">{t('title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('warning')}
        </Typography>

        <Button
          variant="outlined"
          startIcon={syncMutation.isPending ? <CircularProgress size={16} /> : <RefreshIcon />}
          disabled={syncMutation.isPending}
          onClick={() => syncMutation.mutate({ silent: false })}
        >
          {t('syncButton')}
        </Button>
      </Stack>

      <ToggleButtonGroup
        size="small"
        color="primary"
        value={filter}
        exclusive
        onChange={(_event, value: PaymentFilter | null) => {
          if (value !== null) {
            setFilter(value);
          }
        }}
        aria-label={t('filterLabel')}
      >
        <ToggleButton value="ALL">{t('filterAll')}</ToggleButton>
        <ToggleButton value="MATCHED">{t('filterMatched')}</ToggleButton>
        <ToggleButton value="PENDING">
          <Badge
            color="warning"
            badgeContent={pendingCount}
            max={99}
            sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none', ml: 1 } }}
          >
            Čekající
          </Badge>
        </ToggleButton>
      </ToggleButtonGroup>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !filteredPayments || filteredPayments.length === 0 ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 6, color: 'text.secondary' }}>
          <PaymentsIcon fontSize="large" />
          <Typography>{t('emptyState')}</Typography>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          {filteredPayments.map(payment => (
            <PaymentRow
              key={payment.id}
              payment={payment}
              onCancelPending={handleCancelPending}
              onConfirmPending={handleConfirmPending}
              isCancelling={deletePendingMutation.isPending}
              isConfirming={confirmPendingMutation.isPending}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default ReceivedPaymentsPage;
